import { Check } from '@phosphor-icons/react'
import { cn } from '@/shared/lib/utils'

export interface StepperStep {
  label: string
}

export function Stepper({
  steps,
  current,
  ariaLabel,
  formatStepAria,
  sticky = false,
  className,
}: {
  steps: StepperStep[]
  current: number
  ariaLabel: string
  formatStepAria?: (index: number, total: number, label: string) => string
  sticky?: boolean
  className?: string
}) {
  return (
    <nav
      aria-label={ariaLabel}
      className={cn(
        'z-[var(--brand-z-sticky)] border-b border-[var(--brand-border-soft)] bg-[var(--brand-surface)]/95 backdrop-blur',
        sticky && 'sticky top-0',
        className,
      )}
    >
      <ol
        // Scrollable region with no focusable children must itself be keyboard-focusable.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        className="flex items-center gap-2 overflow-x-auto px-1 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-ring)]"
      >
        {steps.map((step, index) => {
          const state = index < current ? 'done' : index === current ? 'active' : 'upcoming'
          const ariaCurrent = state === 'active' ? 'step' : undefined
          const stepAria = formatStepAria?.(index, steps.length, step.label)

          return (
            <li key={index} aria-current={ariaCurrent} aria-label={stepAria} className="flex shrink-0 items-center gap-2">
              <span
                className={cn(
                  'inline-flex size-7 items-center justify-center rounded-pill text-sm font-semibold transition-colors',
                  state === 'done' && 'bg-[var(--brand-success)] text-[var(--brand-on-primary)]',
                  state === 'active' && 'bg-[var(--brand-primary)] text-[var(--brand-on-primary)]',
                  state === 'upcoming' && 'bg-[var(--brand-surface-strong)] text-[var(--brand-text-secondary)]',
                )}
              >
                {state === 'done' ? <Check className="size-4" aria-hidden /> : index + 1}
              </span>
              <span
                className={cn(
                  'text-sm',
                  state === 'active' ? 'font-semibold text-[var(--brand-text-primary)]' : 'text-[var(--brand-text-secondary)]',
                )}
              >
                {step.label}
              </span>
              {index < steps.length - 1 ? <span aria-hidden className="mx-1 h-px w-6 bg-[var(--brand-border-soft)]" /> : null}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
